import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart3, TrendingUp, TrendingDown, Package, FileText, Calendar, Download, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#f97316', '#0ea5e9', '#22c55e', '#eab308', '#ef4444', '#8b5cf6', '#ec4899'];

const Reports = () => {
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState("inventory");
  const [loading, setLoading] = useState(false);
  
  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Report data
  const [inventoryReport, setInventoryReport] = useState(null);
  const [transactionReport, setTransactionReport] = useState(null);
  const [poReport, setPOReport] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchReport();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [activeReport, dateFrom, dateTo, categoryFilter, statusFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      if (activeReport === "inventory") {
        if (categoryFilter && categoryFilter !== "all") params.append('category', categoryFilter);
        const response = await axios.get(`${API_URL}/reports/inventory?${params.toString()}`);
        setInventoryReport(response.data);
      } else if (activeReport === "transactions") {
        const response = await axios.get(`${API_URL}/reports/transactions?${params.toString()}`);
        setTransactionReport(response.data);
      } else if (activeReport === "purchase-orders") {
        if (statusFilter && statusFilter !== "all") params.append('status', statusFilter);
        const response = await axios.get(`${API_URL}/reports/purchase-orders?${params.toString()}`);
        setPOReport(response.data);
      }
    } catch (error) {
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setCategoryFilter("all");
    setStatusFilter("all");
  };

  const exportReport = () => {
    toast.success("Report export coming soon!");
  };

  const renderInventoryReport = () => {
    if (!inventoryReport) return null;
    
    const pieData = inventoryReport.by_category.map((cat, idx) => ({
      name: cat.category,
      value: cat.count,
      color: COLORS[idx % COLORS.length]
    }));

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs text-slate-500 font-bold uppercase">Total Items</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{inventoryReport.summary.total_items}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs text-slate-500 font-bold uppercase">Total Quantity</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{inventoryReport.summary.total_quantity}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-4">
            <p className="text-xs text-orange-700 font-bold uppercase">Low Stock</p>
            <p className="text-2xl font-bold text-orange-700 mt-1">{inventoryReport.summary.low_stock_count}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-xs text-red-700 font-bold uppercase">Out of Stock</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{inventoryReport.summary.out_of_stock}</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Category Bar Chart */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-bold text-slate-900 uppercase text-sm mb-4">Items by Category</h3>
            {inventoryReport.by_category.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No category data</p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={inventoryReport.by_category}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="category" tick={{ fontSize: 11 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Category Pie Chart */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-bold text-slate-900 uppercase text-sm mb-4">Category Distribution</h3>
            {pieData.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Category Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-bold text-slate-900 uppercase text-sm">Category Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Category</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Items</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Total Qty</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Low Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {inventoryReport.by_category.map((cat, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{cat.category}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{cat.count}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{cat.quantity}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={cat.low_stock > 0 ? 'text-orange-600 font-bold' : 'text-slate-500'}>
                        {cat.low_stock}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderTransactionReport = () => {
    if (!transactionReport) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs text-slate-500 font-bold uppercase">Total Transactions</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{transactionReport.summary.total_transactions}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-green-600" size={18} />
              <p className="text-xs text-green-700 font-bold uppercase">Stock In</p>
            </div>
            <p className="text-2xl font-bold text-green-700 mt-1">{transactionReport.summary.total_in}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <div className="flex items-center gap-2">
              <TrendingDown className="text-red-600" size={18} />
              <p className="text-xs text-red-700 font-bold uppercase">Stock Out</p>
            </div>
            <p className="text-2xl font-bold text-red-700 mt-1">{transactionReport.summary.total_out}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
            <p className="text-xs text-blue-700 font-bold uppercase">Net Change</p>
            <p className={`text-2xl font-bold mt-1 ${transactionReport.summary.net_change >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {transactionReport.summary.net_change >= 0 ? '+' : ''}{transactionReport.summary.net_change}
            </p>
          </div>
        </div>

        {/* Daily Trend Chart */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="font-bold text-slate-900 uppercase text-sm mb-4">Daily Transaction Trend</h3>
          {transactionReport.daily_summary.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No transaction data</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={[...transactionReport.daily_summary].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#64748b" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                <Tooltip />
                <Line type="monotone" dataKey="in" stroke="#22c55e" strokeWidth={2} name="Stock In" />
                <Line type="monotone" dataKey="out" stroke="#ef4444" strokeWidth={2} name="Stock Out" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Transaction Type Breakdown */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-bold text-slate-900 uppercase text-sm">By Transaction Type</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Count</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Total Quantity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactionReport.by_type.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className={`font-medium ${item.type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                        {item.type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{item.count}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">{item.total_quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderPOReport = () => {
    if (!poReport) return null;

    const statusData = [
      { name: 'Approved', value: poReport.summary.approved, color: '#22c55e' },
      { name: 'Pending', value: poReport.summary.pending, color: '#eab308' },
      { name: 'Rejected', value: poReport.summary.rejected, color: '#ef4444' }
    ].filter(d => d.value > 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="text-xs text-slate-500 font-bold uppercase">Total POs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{poReport.summary.total_pos}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
            <p className="text-xs text-yellow-700 font-bold uppercase">Pending</p>
            <p className="text-2xl font-bold text-yellow-700 mt-1">{poReport.summary.pending}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4">
            <p className="text-xs text-green-700 font-bold uppercase">Approved</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{poReport.summary.approved}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-xs text-red-700 font-bold uppercase">Rejected</p>
            <p className="text-2xl font-bold text-red-700 mt-1">{poReport.summary.rejected}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
            <p className="text-xs text-blue-700 font-bold uppercase">Items Ordered</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{poReport.summary.total_items_ordered}</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-bold text-slate-900 uppercase text-sm mb-4">PO Status Distribution</h3>
            {statusData.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="font-bold text-slate-900 uppercase text-sm mb-4">POs by Creator Role</h3>
            {poReport.by_role.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={poReport.by_role}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="role" tick={{ fontSize: 11 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                  <Tooltip />
                  <Bar dataKey="approved" stackId="a" fill="#22c55e" name="Approved" />
                  <Bar dataKey="pending" stackId="a" fill="#eab308" name="Pending" />
                  <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rejected" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="reports-page">
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: '-0.02em' }}>
              Reports
            </h1>
            <p className="text-slate-500 mt-1 text-sm md:text-base">Analytics and insights</p>
          </div>
          <Button onClick={exportReport} variant="outline">
            <Download size={16} className="mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'inventory', label: 'Inventory', icon: Package },
          { id: 'transactions', label: 'Transactions', icon: TrendingUp },
          { id: 'purchase-orders', label: 'Purchase Orders', icon: FileText }
        ].map((report) => {
          const Icon = report.icon;
          return (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase rounded-sm transition-colors ${
                activeReport === report.id 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{report.label}</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase">From Date</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1 rounded-sm border-slate-300"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600 font-bold uppercase">To Date</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1 rounded-sm border-slate-300"
            />
          </div>
          
          {activeReport === "inventory" && (
            <div>
              <Label className="text-xs text-slate-600 font-bold uppercase">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="mt-1 rounded-sm border-slate-300">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category} value={cat.category}>{cat.category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {activeReport === "purchase-orders" && (
            <div>
              <Label className="text-xs text-slate-600 font-bold uppercase">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 rounded-sm border-slate-300">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-1">
            <Button onClick={fetchReport} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
              <RefreshCw size={16} className="mr-2" /> Refresh
            </Button>
            <Button onClick={handleClearFilters} variant="outline">
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-slate-500">Loading report...</p>
        </div>
      ) : (
        <>
          {activeReport === "inventory" && renderInventoryReport()}
          {activeReport === "transactions" && renderTransactionReport()}
          {activeReport === "purchase-orders" && renderPOReport()}
        </>
      )}
    </div>
  );
};

export default Reports;
